import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Container, Row, Col, Image } from "react-bootstrap";
import { Copy, ChevronDown, TrendingUp, Plus, UserRound } from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import PostComposer from "./components/post-composer/PostComposer";
import PostCard from "./components/post-card/PostCard";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getCommunityTourSteps from "./communityTourSteps";
import RightSidebar from "./components/right-sidebar/RightSidebar";
import FilterTabs from "./components/filter-tabs/FilterTabs";
import SearchBar from "./components/search-bar/SearchBar";
import ArticleDetailModal from "./components/article-detail-modal/ArticleDetailModal";
import PostDetailModal from "./components/post-detail-modal/PostDetailModal";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import { usePosts, useCategories } from "./hooks";
import communityService from "../../api/communityService";
import { mapPostsFromAPI } from "./adapters/postAdapter";
import {
  getRoles,
  getAllStates,
  getAllCities,
} from "../../redux/slices/authSlice";
import { submitRequestedRole } from "../../redux/slices/communitySlice";
import { fetchCommunityUsage } from "../../redux/slices/communityAllowanceSlice";
import { getViewProfile } from "../../redux/slices/profileSlice";
import {
  PlanShield,
  UpgradePlanModal,
} from "../../components/subscription/UpgradePlanModal";
import { allowedNames, allowedRolesForFree } from "../../constants";
import ComparisonModal from "../restriction-modals/ComparisonModal";
import RestrictionModal from "../restriction-modals/RestrictionModal";
import usePlan from "../../hooks/usePlan";
// Reuse the popover styles built for the Search page chip filters
// (`.mmp-chip-popover`, `.chip-option`, etc. are declared at the top
// level there, so importing this file just adds those rules globally).
import "../search/searchTopbar.scss";
import "./styles/Community.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
  FaInstagram,
} from "react-icons/fa6";
import usePageTracking from "../../hooks/usePageTracking";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../utils/profileCompletion";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown —
// photo/name affect how the user appears when posting/commenting, city
// affects location-relevant community content.
const COMMUNITY_RELEVANT_PROFILE_NUDGES = [
  {
    key: "photo",
    message: "Add a profile photo so others recognize you in the community.",
  },
  {
    key: "full_name",
    message: "Add your full name so it shows on your posts and comments.",
  },
  {
    key: "city",
    message: "Add your city to see more relevant community discussions.",
  },
];

// ============= Profession chip filter =============
// Mirrors the Role chip on Search.js: portaled popover, multi-select,
// plan-gated locked options. Trigger reuses the existing `.discussion-filter`
// styling so the visual stays identical to the other community filter pills.
const PROFESSION_POPOVER_MIN_W = 240;
const PROFESSION_POPOVER_MAX_W = 320;
const PROFESSION_POPOVER_GAP = 6;

// Short labels for the chip trigger (full name fits the popover)
// Static specialty list — sent to /get-all-posts as `specialities`
const SPECIALTY_OPTIONS = [
  "Emergency Room (ER)",
  "Operating Room (OR)",
  "Cardiac Care Unit (CCU)",
  "Medical-Surgical",
  "ICU",
  "Pediatrics",
  "NICU",
  "Telemetry",
  "Oncology",
  "L&D",
  "Psych",
  "Rehab",
  "Case Management",
  "Wound Care",
  "Other",
].map((name) => ({ value: name, label: name }));

// Short labels shown on the Specialty chip when one is selected
const SPECIALTY_SHORT_LABELS = {
  "Emergency Room (ER)": "ER",
  "Operating Room (OR)": "OR",
  "Cardiac Care Unit (CCU)": "CCU",
  "Medical-Surgical": "Med-Surg",
  "Case Management": "Case Mgmt",
  "Wound Care": "Wound",
};

const specialtyChipLabel = (selected, fallback) => {
  if (!Array.isArray(selected) || selected.length === 0) return fallback;
  if (selected.length === 1) {
    const { label } = selected[0];
    return SPECIALTY_SHORT_LABELS[label] || label;
  }
  return `${selected.length} selected`;
};

const PROFESSION_SHORT_LABELS = {
  "Home Health and Personal Care Aides": "HHA/PCA",
  "Licensed Practical and Licensed Vocational Nurses": "LPN/LVN",
  "Medical and Health Services Managers": "MHSM",
  "Nurse Anesthetists": "CRNA",
  "Nurse Midwives": "CNM",
  "Nurse Practitioners": "NP",
  "Nursing Assistants": "CNA",
  "Nursing Instructors and Teachers Postsecondary": "Nursing Faculty",
  "Registered Nurses": "RN",
};

const chipShortLabel = (selected, fallback) => {
  if (!Array.isArray(selected) || selected.length === 0) return fallback;
  if (selected.length === 1) {
    const { label } = selected[0];
    return PROFESSION_SHORT_LABELS[label] || label;
  }
  return `${selected.length} selected`;
};


const ChipChevSmall = ({ open = false }) => (
  <ChevronDown
    size={14}
    className="filter-caret"
    style={{
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform 0.15s ease",
    }}
  />
);

const ChipCheckSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function ChipFilterDropdown({
  label,
  value,
  active,
  options,
  selected,
  onApply,
  onLockedTap,
  dataTour,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const chipRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!chipRef.current) return;
      const rect = chipRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const PAD = 8; // viewport edge padding

      // Start from the chip's natural width, clamp to min/max, then
      // make sure the popover never exceeds the available viewport.
      const maxFit = Math.max(120, vw - 2 * PAD);
      let width = Math.min(
        PROFESSION_POPOVER_MAX_W,
        Math.max(PROFESSION_POPOVER_MIN_W, rect.width),
      );
      if (width > maxFit) width = maxFit;

      // Position: prefer aligning with the chip's left edge, but never
      // let the popover overflow on either side (handles the mobile case
      // where the chip itself is partially off-screen because the parent
      // row has overflow-x: auto).
      let left = rect.left;
      if (left + width > vw - PAD) left = rect.right - width; // try right-anchor
      if (left + width > vw - PAD) left = vw - PAD - width; // clamp right
      if (left < PAD) left = PAD; // clamp left

      setPos({
        top: rect.bottom + PROFESSION_POPOVER_GAP,
        left,
        width,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (chipRef.current && chipRef.current.contains(e.target)) return;
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isSelected = (opt) =>
    Array.isArray(selected) && selected.some((s) => s.value === opt.value);

  const pick = (opt) => {
    if (opt.isDisabled) {
      setOpen(false);
      onLockedTap?.(opt);
      return;
    }
    const arr = Array.isArray(selected) ? selected : [];
    const next = isSelected(opt)
      ? arr.filter((s) => s.value !== opt.value)
      : [...arr, opt];
    onApply(next);
  };

  const clear = () => onApply([]);
  const hasSelection = Array.isArray(selected) && selected.length > 0;

  const popover =
    open &&
    pos &&
    createPortal(
      <div
        ref={popoverRef}
        className="mmp-chip-popover"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          width: pos.width,
          maxWidth: pos.width,
          zIndex: 9500,
        }}
        role="listbox"
        aria-multiselectable
      >
        {(!options || options.length === 0) && (
          <div
            className="chip-option"
            style={{
              color: "var(--mmp-text-secondary)",
              cursor: "default",
              background: "transparent",
            }}
          >
            Loading…
          </div>
        )}
        {options.map((opt, i) => {
          const sel = isSelected(opt);
          return (
            <button
              key={`${String(opt.value)}-${i}`}
              type="button"
              className={`chip-option ${sel ? "is-selected" : ""} ${
                opt.isDisabled ? "is-locked" : ""
              }`}
              onClick={() => pick(opt)}
              role="option"
              aria-selected={sel}
              aria-disabled={opt.isDisabled || false}
            >
              <span className="chip-check" aria-hidden="true">
                {sel && <ChipCheckSvg />}
              </span>
              <span className="chip-option-label">{opt.label}</span>
              {opt.requiredPlan && !sel && (
                <span className="chip-option-badge">{opt.requiredPlan}</span>
              )}
            </button>
          );
        })}
        {hasSelection && (
          <button type="button" className="chip-clear" onClick={clear}>
            Clear
          </button>
        )}
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        data-tour={dataTour}
        className={`discussion-filter ${active ? "is-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="filter-label">{label}:</span>
        <strong className="filter-value">{value}</strong>
        <ChipChevSmall open={open} />
      </button>
      {popover}
    </>
  );
}

// ============= Location chip filter =============
// Two-step popover: pick a state, then pick a city. Each step has its own
// in-popover search input. State/city lists come from authSlice (preloaded
// with getAllStates / getAllCities).
function LocationDropdown({
  value,
  active,
  states,
  cities,
  selectedState,
  selectedCity,
  citiesLoading,
  onSelectState,
  onClearState,
  onSelectCity,
  dataTour,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const chipRef = useRef(null);
  const popoverRef = useRef(null);

  // Reset the local search box whenever the popover transitions to the
  // city view (new state picked) or back to the state view.
  useEffect(() => {
    if (selectedState) {
      setCitySearch("");
    } else {
      setStateSearch("");
    }
  }, [selectedState]);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!chipRef.current) return;
      const rect = chipRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const PAD = 8; // viewport edge padding

      // Start from the chip's natural width, clamp to min/max, then
      // make sure the popover never exceeds the available viewport.
      const maxFit = Math.max(120, vw - 2 * PAD);
      let width = Math.min(
        PROFESSION_POPOVER_MAX_W,
        Math.max(PROFESSION_POPOVER_MIN_W, rect.width),
      );
      if (width > maxFit) width = maxFit;

      // Position: prefer aligning with the chip's left edge, but never
      // let the popover overflow on either side (handles the mobile case
      // where the chip itself is partially off-screen because the parent
      // row has overflow-x: auto).
      let left = rect.left;
      if (left + width > vw - PAD) left = rect.right - width; // try right-anchor
      if (left + width > vw - PAD) left = vw - PAD - width; // clamp right
      if (left < PAD) left = PAD; // clamp left

      setPos({
        top: rect.bottom + PROFESSION_POPOVER_GAP,
        left,
        width,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (chipRef.current && chipRef.current.contains(e.target)) return;
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filteredStates = (states || []).filter((s) => {
    const q = stateSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.state_full_name || "").toLowerCase().includes(q) ||
      (s.state || "").toLowerCase().includes(q)
    );
  });

  const filteredCities = (cities || []).filter((c) => {
    const q = citySearch.trim().toLowerCase();
    if (!q) return true;
    return (c.city || "").toLowerCase().includes(q);
  });

  const searchInputStyle = {
    width: "100%",
    padding: "6px 10px",
    margin: "2px 0 6px",
    border: "1px solid var(--mmp-border)",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 500,
    color: "var(--mmp-text-primary)",
    outline: "none",
    background: "var(--mmp-hover-bg)",
  };

  const headerRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "4px 6px",
    margin: "2px 0 6px",
    background: "var(--mmp-accent-bg)",
    border: "1px solid var(--mmp-accent-border)",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 700,
    color: "var(--mmp-accent-hover)",
  };

  const popover =
    open &&
    pos &&
    createPortal(
      <div
        ref={popoverRef}
        className="mmp-chip-popover"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          width: pos.width,
          maxWidth: pos.width,
          zIndex: 9500,
        }}
        role="listbox"
      >
        {(selectedState || selectedCity) && (
          <button
            type="button"
            className="chip-clear"
            onClick={() => {
              onClearState();
              setOpen(false);
            }}
            style={{
              borderTop: "none",
              borderBottom: "1px solid var(--mmp-border)",
              marginTop: 0,
              marginBottom: 4,
            }}
          >
            Clear all
          </button>
        )}
        {!selectedState && (
          <>
            <input
              type="text"
              value={stateSearch}
              onChange={(e) => setStateSearch(e.target.value)}
              placeholder="Search state…"
              style={searchInputStyle}
              autoFocus
            />
            {(!states || states.length === 0) && (
              <div
                className="chip-option"
                style={{
                  color: "var(--mmp-text-secondary)",
                  cursor: "default",
                  background: "transparent",
                }}
              >
                Loading…
              </div>
            )}
            {states && states.length > 0 && filteredStates.length === 0 && (
              <div
                className="chip-option"
                style={{
                  color: "var(--mmp-text-secondary)",
                  cursor: "default",
                  background: "transparent",
                }}
              >
                No states found
              </div>
            )}
            {filteredStates.map((s) => (
              <button
                key={s.state}
                type="button"
                className="chip-option"
                onClick={() => onSelectState(s)}
                role="option"
              >
                <span className="chip-option-label">
                  {s.state_full_name || s.state}
                </span>
              </button>
            ))}
          </>
        )}

        {selectedState && (
          <>
            <div style={headerRowStyle}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedState.state_full_name || selectedState.state}
              </span>
              <button
                type="button"
                onClick={onClearState}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--mmp-accent-hover)",
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: "2px 6px",
                }}
              >
                Change
              </button>
            </div>
            <input
              type="text"
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              placeholder="Search city…"
              style={searchInputStyle}
              autoFocus
            />
            {citiesLoading && (
              <div
                className="chip-option"
                style={{
                  color: "var(--mmp-text-secondary)",
                  cursor: "default",
                  background: "transparent",
                }}
              >
                Loading…
              </div>
            )}
            {!citiesLoading && filteredCities.length === 0 && (
              <div
                className="chip-option"
                style={{
                  color: "var(--mmp-text-secondary)",
                  cursor: "default",
                  background: "transparent",
                }}
              >
                No cities found
              </div>
            )}
            {!citiesLoading &&
              filteredCities.map((c, i) => {
                const isSel = selectedCity && selectedCity.city === c.city;
                return (
                  <button
                    key={`${c.city}-${i}`}
                    type="button"
                    className={`chip-option ${isSel ? "is-selected" : ""}`}
                    onClick={() => {
                      onSelectCity(c);
                      setOpen(false);
                    }}
                    role="option"
                    aria-selected={isSel}
                  >
                    <span className="chip-option-label">{c.city}</span>
                  </button>
                );
              })}
          </>
        )}
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        data-tour={dataTour}
        className={`discussion-filter ${active ? "is-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="filter-label">Location:</span>
        <strong className="filter-value">{value}</strong>
        <ChipChevSmall open={open} />
      </button>
      {popover}
    </>
  );
}

/**
 * Community Component
 * Main container for community posts feed
 * Handles posts fetching, filtering, searching with full API integration
 * Uses optimistic updates for like/unlike operations
 */
const Community = () => {
  // Guided tour — auto-starts on first visit, replayable from the header.
  const communityTourSteps = useMemo(() => getCommunityTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("community");

  // ============= HOOKS =============
  // Posts management hook with all CRUD operations
  const {
    posts,
    loading: postsLoading,
    error: postsError,
    fetchPosts,
    createPost,
    toggleLikePost,
    deletePost,
    reportPost,
    updateCommentCount,
    addOrUpdateReaction,
    loadMore,
    hasMore,
    refresh,
    pagination,
  } = usePosts();

  // Categories for filtering
  const { categories, loading: categoriesLoading } = useCategories();

  // Free users get a small weekly post allowance (1/week); paid plans are
  // unlimited. The counter comes from the backend via Redux.
  const { isFree } = usePlan();
  const postAllowance = useSelector((state) => state.communityAllowance.post);
  const [showCreatePostRestriction, setShowCreatePostRestriction] = useState(false);
  // Upgrade-plan modal opened by tapping the plan shield in the profile strip.
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  // remaining posts to show in the UI (null when unlimited or not yet loaded)
  const postsRemaining =
    isFree && postAllowance && postAllowance.limit >= 0
      ? postAllowance.remaining
      : null;
  // Block only once a free user has actually used up their weekly allowance.
  // If the allowance hasn't loaded yet we let them through — the backend still
  // enforces the cap and returns a 403 with an upgrade prompt.
  const blockCreateIfFree = () => {
    if (postsRemaining !== null && postsRemaining <= 0) {
      setShowCreatePostRestriction(true);
      return true;
    }
    return false;
  };

  // ============= LOCAL STATE =============
  const [activeFilter, setActiveFilter] = useState("All posts");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState(null); // null = 'New', 'weekly', 'monthly', 'yearly', 'all'

  // Trending-period dropdown (same options as the old SearchBar sort menu).
  const trendingSortOptions = [
    { value: null, label: "New" },
    // { value: null, label: "This week" },
    { value: "weekly", label: "Top (Weekly)" },
    { value: "monthly", label: "Top (Monthly)" },
    { value: "yearly", label: "Top (Yearly)" },
    { value: "all", label: "Top (All Time)" },
  ];
  const [showTrendingSort, setShowTrendingSort] = useState(false);
  const [trendingSortPos, setTrendingSortPos] = useState(null);
  const trendingSortRef = useRef(null);
  const trendingSortMenuRef = useRef(null);
  const trendingSortLabel =
    trendingSortOptions.find((o) => o.value === sortBy)?.label || "New";

  // Position the portaled dropdown under the trigger; recompute on
  // scroll / resize so it follows the button.
  useEffect(() => {
    if (!showTrendingSort) return undefined;
    const update = () => {
      if (!trendingSortRef.current) return;
      const rect = trendingSortRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const width = 180;
      // Right-align to the trigger; clamp inside the viewport.
      let left = rect.right - width;
      if (left + width > vw - 8) left = vw - 8 - width;
      if (left < 8) left = 8;
      setTrendingSortPos({ top: rect.bottom + 6, left, width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showTrendingSort]);

  // Close trending-period dropdown when clicking outside (trigger or menu)
  useEffect(() => {
    if (!showTrendingSort) return undefined;
    const onDown = (e) => {
      if (trendingSortRef.current && trendingSortRef.current.contains(e.target))
        return;
      if (
        trendingSortMenuRef.current &&
        trendingSortMenuRef.current.contains(e.target)
      )
        return;
      setShowTrendingSort(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showTrendingSort]);
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pendingPostText, setPendingPostText] = useState(null);

  // Content tab — drives the `feed` param on /get-all-posts
  // UI key -> API value: 'for-you' -> 'for_you', others map 1:1
  const [contentTab, setContentTab] = useState("all");

  // Maps UI tab -> `feed` value on /get-all-posts.
  // Returns null for "saved" — that tab uses a different API, so we don't
  // include `feed` in the get-all-posts payload for it.
  const feedFromTab = (tab) => {
    switch (tab) {
      case "for-you":
        return "for_you";
      case "following":
        return "following";
      case "all":
        return "all";
      case "saved":
      default:
        return null;
    }
  };

  // ============= SAVED POSTS (separate API: GET /frontend/post-saves) =============
  const SAVED_PAGE_LIMIT = 10;
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedPage, setSavedPage] = useState(1);
  const [savedHasMore, setSavedHasMore] = useState(false);

  /**
   * Fetch saved posts (GET /frontend/post-saves?page&limit).
   * Response shape is treated flexibly so it works whether the backend
   * returns the same `data.posts.{data,page,pages,...}` envelope as
   * /get-all-posts or a flatter `data.{data,page,pages,...}` shape.
   */
  const fetchSavedPosts = useCallback(async (page = 1, append = false) => {
    try {
      setSavedLoading(true);
      const response = await communityService.getSavedPosts(
        page,
        SAVED_PAGE_LIMIT,
      );

      const root = response?.data?.data?.posts || response?.data?.data || {};
      const rawList = Array.isArray(root) ? root : root.data || [];
      const totalPages =
        root.pages || root.total_pages || (rawList.length ? page : 0);
      const currentPage = root.page || page;

      const mapped = mapPostsFromAPI(rawList).filter((p) => p && p.id);

      setSavedPosts((prev) => {
        if (!append) return mapped;
        const existingIds = new Set(prev.map((p) => p.id));
        return [...prev, ...mapped.filter((p) => !existingIds.has(p.id))];
      });
      setSavedPage(currentPage);
      setSavedHasMore(currentPage < totalPages);
    } catch (err) {
      console.error("Error fetching saved posts:", err);
      if (err?.message !== "Token expired. Please login again.") {
        toast.error("Failed to load saved posts. Please try again.");
      }
    } finally {
      setSavedLoading(false);
    }
  }, []);

  const loadMoreSaved = useCallback(() => {
    if (savedLoading || !savedHasMore) return;
    fetchSavedPosts(savedPage + 1, true);
  }, [fetchSavedPosts, savedLoading, savedHasMore, savedPage]);

  // Wrap refresh so post-create / external refresh stays on the current feed tab.
  // Saved tab uses a different API, so route the refresh there.
  const refreshCurrentTab = useCallback(
    (filters = {}) => {
      if (contentTab === "saved") {
        return fetchSavedPosts(1, false);
      }
      const feed = feedFromTab(contentTab);
      const roleIds = (currentFiltersRef.current.roles || []).filter(Boolean);
      const specialityNames = (
        currentFiltersRef.current.specialities || []
      ).filter(Boolean);
      return refresh({
        ...(feed && { feed }),
        ...(roleIds.length > 0 && { roles: roleIds }),
        ...(specialityNames.length > 0 && { specialities: specialityNames }),
        ...filters,
      });
    },
    [refresh, contentTab, fetchSavedPosts],
  );

  // Current user (for the profile strip below the search bar)
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth?.user);
  const authStore = useSelector((state) => state.auth);

  // Load the free-tier weekly community allowance (posts/comments left) on mount.
  useEffect(() => {
    dispatch(fetchCommunityUsage());
  }, [dispatch]);
  // ============= Profession (role) chip filter =============
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleLiveOptions, setRoleLiveOptions] = useState([]);

  // ============= Specialty chip filter (static list) =============
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const handleSpecialtyApply = useCallback((next) => {
    setSelectedSpecialties(next);
  }, []);

  // ============= Location chip filter (state → city) =============
  const [selectedLocationState, setSelectedLocationState] = useState(null);
  const [selectedLocationCity, setSelectedLocationCity] = useState(null);
  const [locationCitiesLoading, setLocationCitiesLoading] = useState(false);

  const allStates = authStore?.stateItems?.states || [];
  const allCities = authStore?.cityItems?.cities || [];

  // Preload states once
  useEffect(() => {
    if (!Array.isArray(allStates) || allStates.length === 0) {
      dispatch(getAllStates());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch cities whenever a state is picked
  useEffect(() => {
    if (!selectedLocationState) return;
    setLocationCitiesLoading(true);
    Promise.resolve(
      dispatch(getAllCities({ search_state: selectedLocationState.state })),
    ).finally(() => setLocationCitiesLoading(false));
  }, [selectedLocationState, dispatch]);

  const handleSelectLocationState = useCallback((s) => {
    setSelectedLocationState(s);
    setSelectedLocationCity(null);
  }, []);

  const handleClearLocationState = useCallback(() => {
    setSelectedLocationState(null);
    setSelectedLocationCity(null);
  }, []);

  const handleSelectLocationCity = useCallback((c) => {
    setSelectedLocationCity(c);
  }, []);

  // Chip label: City > State > "Any"
  const locationChipValue = selectedLocationCity?.city
    ? selectedLocationCity.city
    : selectedLocationState
    ? selectedLocationState.state_full_name || selectedLocationState.state
    : "Any";

  // Location payload value sent to /get-all-posts.
  // "City, ST" when both picked, "ST" when only state is picked, null otherwise.
  const locationFilterValue =
    selectedLocationCity?.city && selectedLocationState?.state
      ? `${selectedLocationCity.city}, ${selectedLocationState.state}`
      : selectedLocationState?.state || null;
  // Restriction modal state — mirrors Search.js so the locked-role tap
  // opens the same ComparisonModal with the same Plus/Pro messaging.
  const [subAlertCompModalVisible, setSubAlertCompModalVisible] = useState(
    false,
  );
  const [upgradeSource, setUpgradeSource] = useState(null);
  const [subText, setSubText] = useState({
    header: "",
    desc: "",
    massage1: "",
    pluspricem: "",
    pluspricey: "",
    desc2: "",
    promassage: "",
    propricem: "",
    propricey: "",
  });

  // ===== Wishlist role input (community feed waitlist card) =====
  // Local input + submit-in-flight flag. The "already submitted" state
  // is derived from profileData.requested_role so it survives a refresh.
  const [wishlistRoleText, setWishlistRoleText] = useState("");
  const [wishlistSubmitting, setWishlistSubmitting] = useState(false);

  const handleWishlistRoleSubmit = useCallback(async () => {
    const value = wishlistRoleText.trim();
    if (!value || wishlistSubmitting) return;
    setWishlistSubmitting(true);
    try {
      const action = await dispatch(submitRequestedRole({ role_name: value }));
      if (submitRequestedRole.fulfilled.match(action)) {
        setWishlistRoleText("");
        // Refresh the profile so requested_role is populated and the
        // card switches to its "thanks" state on the next render.
        dispatch(getViewProfile());
        toast.success("Thanks — we've recorded your suggestion");
      } else {
        toast.error("Could not submit your suggestion. Try again.");
      }
    } finally {
      setWishlistSubmitting(false);
    }
  }, [dispatch, wishlistRoleText, wishlistSubmitting]);

  // Load roles from the auth slice once.
  // Initial redux state is `roleItems: { describe_roles: [] }`, so check
  // the array length — not just `roleItems` truthiness — before deciding
  // whether to dispatch.
  useEffect(() => {
    const cached = authStore?.roleItems?.describe_roles;
    if (!Array.isArray(cached) || cached.length === 0) {
      dispatch(getRoles());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the option list with plan gating — same rules as Search.js.
  // The API can return placeholder rows with `name: null` (ids 11–34 at
  // the moment) — drop those so they don't render as blank chips.
  useEffect(() => {
    const describe = authStore?.roleItems?.describe_roles;
    if (!Array.isArray(describe) || describe.length === 0) return;

    const currentPlan = authStore?.user?.plan?.name;
    const data = describe
      .filter((s) => s && typeof s.name === "string" && s.name.trim() !== "")
      .map((s) => {
        const roleName = s.name;
        let isDisabled = false;
        let requiredPlan = "";

        if (currentPlan === "Free") {
          if (!allowedRolesForFree.includes(roleName)) {
            isDisabled = true;
            requiredPlan = "Pro";
          }
        }

        return { value: s.id, label: roleName, isDisabled, requiredPlan };
      });

    setRoleLiveOptions(data);
  }, [authStore?.roleItems, authStore?.user?.plan?.name]);

  // No default selection — the Profession chip stays in the inactive
  // "All" state until the user picks a role from the popover.

  // Open the comparison/upgrade modal when a user taps a plan-gated role
  // in the chip popover — uses the same copy as Search.js.
  const handleLockedProfessionTap = useCallback((opt) => {
    setUpgradeSource("nursing_role_limit");
    if (opt.requiredPlan === "Pro") {
      setSubText({
        header: "Subscription Required!",
        desc: "",
        massage1: "",
        massage2: "",
        desc2: "You need a Pro plan to unlock this role",
        promassage:
          "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
        propricem: "$2.99/month",
        propricey: "$29.99/year",
        plan: "Pro",
        lockedItemName: opt.label,
      });
    }
    setSubAlertCompModalVisible(true);
  }, []);

  const handleProfessionApply = useCallback((next) => {
    setSelectedRoles(next);
  }, []);

  const profileFullName =
    authUser?.full_name ||
    `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() ||
    "User";
  const profileInitials = profileFullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
  // Location for the profile strip.
  // `authUser.state` is often missing — the canonical state code lives on
  // the profile slice as `living_state` (and is merged onto authUser when
  // /profile loads). Read from both places, in order, and stitch with
  // the city only when we actually have a value.
  const profileData = useSelector(
    (state) => state.profile?.profileItem?.data || null,
  );

  // "Complete your profile" nudge shown below the title, in its own card —
  // picks the single highest-priority missing field rather than a generic
  // percentage, since that's more actionable here. Dismissal is in-memory
  // only (not persisted) so the nudge reappears on reload, re-login, or a
  // fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const profileCompletionPercent = getProfileCompletionPercent(profileData);
  const activeProfileNudge = profileData
    ? COMMUNITY_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(profileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  const locCity =
    authUser?.city ||
    profileData?.city ||
    profileData?.living_city ||
    authUser?.work_city ||
    profileData?.work_city ||
    "";
  const locState =
    authUser?.state ||
    authUser?.living_state ||
    profileData?.living_state ||
    authUser?.work_state ||
    profileData?.work_state ||
    "";
  const location =
    locCity && locState ? `${locCity}, ${locState}` : locCity || locState || "";

  // Show the short form of the user's primary specialty when we have one
  // mapped (e.g. "Medical-Surgical" -> "Med-Surg"); fall back to the raw value.
  const primarySpecialtyRaw = authUser?.nurseProfile?.primary_specialty;
  const primarySpecialtyShort = primarySpecialtyRaw
    ? SPECIALTY_SHORT_LABELS[primarySpecialtyRaw] || primarySpecialtyRaw
    : "";

  // Same idea for the nursing role — abbreviate using PROFESSION_SHORT_LABELS
  // (e.g. "Licensed Practical and Licensed Vocational Nurses" -> "LPN/LVN").
  const nursingRoleRaw = authUser?.nursing_role?.role_title;
  const nursingRoleShort = nursingRoleRaw
    ? PROFESSION_SHORT_LABELS[nursingRoleRaw] || nursingRoleRaw
    : "";

  const profileMetaParts = [
    nursingRoleShort,
    primarySpecialtyShort,
    location,
  ].filter(Boolean);

  // ============= POLL STATE =============
  const [activePoll, setActivePoll] = useState(null);
  // For single-select: number | null. For multi-select: number[]
  const [pollSelectedOptions, setPollSelectedOptions] = useState([]);
  const [pollSubmitted, setPollSubmitted] = useState(false);
  const [pollSubmitting, setPollSubmitting] = useState(false);

  // Fetch active poll once on mount
  useEffect(() => {
    communityService.getActivePoll()
      .then((res) => {
        const poll = res?.data?.data?.poll || null;
        if (!poll) return;
        setActivePoll(poll);
        // If the user already voted, pre-select their answer(s) and show submitted state
        const votedIds = poll.user_voted_option_ids || (poll.user_voted_option_id ? [poll.user_voted_option_id] : []);
        if (votedIds.length > 0) {
          setPollSelectedOptions(votedIds);
          setPollSubmitted(true);
        }
      })
      .catch((err) => {
        console.error('Failed to load poll:', err);
      });
  }, []);

  const handlePollOptionChange = useCallback((optionId) => {
    if (pollSubmitted) return;
    if (activePoll?.is_multi_select) {
      // Toggle the option in/out of the selection array
      setPollSelectedOptions((prev) =>
        prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
      );
    } else {
      // Single select — replace
      setPollSelectedOptions([optionId]);
    }
  }, [pollSubmitted, activePoll]);

  const handlePollSubmit = useCallback(async () => {
    if (pollSelectedOptions.length === 0 || !activePoll || pollSubmitting) return;
    setPollSubmitting(true);
    try {
      const payload = activePoll.is_multi_select ? pollSelectedOptions : pollSelectedOptions[0];
      await communityService.submitPollVote(activePoll.id, payload);
      // Optimistically update vote counts for all selected options
      setActivePoll((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          options: prev.options.map((opt) =>
            pollSelectedOptions.includes(opt.id)
              ? { ...opt, vote_count: (opt.vote_count || 0) + 1 }
              : opt
          ),
          total_votes: (prev.total_votes || 0) + pollSelectedOptions.length,
        };
      });
      setPollSubmitted(true);
    } catch (err) {
      console.error('Poll vote failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to submit your vote. Please try again.');
    } finally {
      setPollSubmitting(false);
    }
  }, [pollSelectedOptions, activePoll, pollSubmitting]);

  // Modal state for article detail
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Post-detail modal — opening a post here (instead of navigating to
  // /community/:id) keeps the feed mounted so scroll position is preserved
  // and the feed isn't refetched when the modal closes.
  const [detailPostId, setDetailPostId] = useState(null);
  const [detailPostPlanName, setDetailPostPlanName] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const handleOpenPostDetail = useCallback((postId, planName) => {
    setDetailPostId(postId);
    setDetailPostPlanName(planName || null);
    setShowDetailModal(true);
  }, []);

  const handleClosePostDetail = useCallback(() => {
    setShowDetailModal(false);
    setDetailPostId(null);
    setDetailPostPlanName(null);
  }, []);

  // Modal state for share post
  const [shareModal, setShareModal] = useState(false);
  const [currentSharePost, setCurrentSharePost] = useState(null);

  // Infinite scroll - ref to detect when user scrolls to bottom
  const loaderRef = useRef(null);
  // feed: "all" matches the default contentTab; becomes null on Saved tab
  // so the infinite-scroll loader can detect & skip /get-all-posts there.
  const currentFiltersRef = useRef({
    search: "",
    category: null,
    sortBy: null,
    feed: "all",
    roles: [],
    specialities: [],
  });

  // ============= CHECK FOR PENDING POST TEXT =============
  /**
   * Check localStorage for pendingPostText on component mount
   * If found, trigger the post composer modal with the text
   */
  useEffect(() => {
    const storedPostText = localStorage.getItem("pendingPostText");
    if (storedPostText) {
      setPendingPostText(storedPostText);
      localStorage.removeItem("pendingPostText"); // Clean up immediately
    }
  }, []);

  // Activity Tracking

  useEffect(() => {
    const payload = {
      page_name: "COMMUNITY",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "COMMUNITY",
        }),
      );
    };
  }, [dispatch]);
  usePageTracking("COMMUNITY");

  // ============= EFFECTS =============
  /**
   * Load initial posts on component mount
   */
  useEffect(() => {
    const loadInitialPosts = async () => {
      try {
        // Saved tab has its own API
        if (contentTab === "saved") {
          await fetchSavedPosts(1, false);
          setIsInitialLoad(false);
          return;
        }
        await fetchPosts({
          page: 1,
          length: 10,
          feed: feedFromTab(contentTab),
        });
        setIsInitialLoad(false);
      } catch (error) {
        console.error("Error loading initial posts:", error);
        setIsInitialLoad(false);
      }
    };

    loadInitialPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  /**
   * Apply filters and search when they change.
   * Debounced (500ms) so typing in the search box doesn't fire a request
   * on every keystroke. Tab changes have their own effect below — they
   * fire immediately without debouncing.
   * Skip on initial load to prevent duplicate API calls.
   */
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    const searchTimer = setTimeout(() => {
      handleApplyFilters();
    }, 500);

    return () => clearTimeout(searchTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeFilter,
    searchTerm,
    sortBy,
    selectedRoles,
    selectedSpecialties,
    selectedLocationCity,
    selectedLocationState,
  ]);

  /**
   * Tab change — refetch immediately every time, even if the user
   * returns to a previously visited tab. No debounce, no caching.
   * Also reset sortBy when switching to for-you since that tab has its own ranking.
   */
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }
    if (contentTab === "for-you" && sortBy) {
      setSortBy(null);
      return; // setSortBy triggers the filter effect above; no need to also call handleApplyFilters here
    }
    handleApplyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentTab]);

  /**
   * Infinite Scroll - Facebook Level
   * Uses Intersection Observer to detect when user scrolls near bottom
   * Automatically loads next page when loader element becomes visible
   */
  useEffect(() => {
    const loaderElement = loaderRef.current;
    if (!loaderElement) return;

    // Create Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (!firstEntry.isIntersecting) return;

        // Pause pagination while the guided tour drives the scroll — otherwise
        // scrolling to the post-card steps keeps loading more posts.
        if (document.body.classList.contains("mmp-tour-active")) return;

        // Saved tab uses its own paging
        if (contentTab === "saved") {
          if (savedHasMore && !savedLoading) {
            loadMoreSaved();
          }
          return;
        }

        // If loader is visible and we have more pages to load
        if (hasMore && !postsLoading) {
          console.log("🔄 [INFINITE SCROLL] Loading more posts...");

          // Get current filters for loadMore
          const filters = {};
          if (currentFiltersRef.current.search) {
            filters.search = currentFiltersRef.current.search;
          }
          if (currentFiltersRef.current.category) {
            filters.category = currentFiltersRef.current.category;
          }
          if (currentFiltersRef.current.sortBy) {
            filters.sortBy = currentFiltersRef.current.sortBy;
          }
          if (currentFiltersRef.current.feed) {
            filters.feed = currentFiltersRef.current.feed;
          }
          if (
            Array.isArray(currentFiltersRef.current.roles) &&
            currentFiltersRef.current.roles.length > 0
          ) {
            filters.roles = currentFiltersRef.current.roles;
          }
          if (
            Array.isArray(currentFiltersRef.current.specialities) &&
            currentFiltersRef.current.specialities.length > 0
          ) {
            filters.specialities = currentFiltersRef.current.specialities;
          }
          if (currentFiltersRef.current.location) {
            filters.location = currentFiltersRef.current.location;
          }

          loadMore(filters);
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: "200px", // Trigger 200px before reaching the loader
        threshold: 0.1, // Trigger when 10% of loader is visible
      },
    );

    observer.observe(loaderElement);

    // Cleanup
    return () => {
      if (loaderElement) {
        observer.unobserve(loaderElement);
      }
    };
  }, [
    hasMore,
    postsLoading,
    loadMore,
    contentTab,
    savedHasMore,
    savedLoading,
    loadMoreSaved,
  ]);

  // ============= HANDLERS =============
  /**
   * Apply filters and search to posts
   * Calls API with filter parameters
   */
  const handleApplyFilters = useCallback(async () => {
    try {
      setIsSearching(true);

      // Find category ID from category name
      let categoryId = null;
      if (activeFilter && activeFilter !== "All posts") {
        const category = categories?.find(
          (cat) => cat.category_name === activeFilter,
        );
        categoryId = category?.id || null;
      }

      const feed = feedFromTab(contentTab);
      const roleIds = selectedRoles.map((r) => r.value);
      const specialityNames = selectedSpecialties.map((s) => s.value);

      // Store current filters for infinite scroll
      currentFiltersRef.current = {
        search: searchTerm || "",
        category: categoryId || null,
        sortBy: sortBy || null,
        feed,
        roles: roleIds,
        specialities: specialityNames,
        location: locationFilterValue || null,
      };

      // Saved tab uses a dedicated API (search/category/sort don't apply there)
      if (contentTab === "saved") {
        await fetchSavedPosts(1, false);
        return;
      }

      await fetchPosts({
        page: 1,
        length: 10,
        ...(feed && { feed }),
        ...(searchTerm && { search: searchTerm }),
        ...(categoryId && { category: categoryId }),
        ...(sortBy && { sortBy: sortBy }),
        ...(roleIds.length > 0 && { roles: roleIds }),
        ...(specialityNames.length > 0 && { specialities: specialityNames }),
        ...(locationFilterValue && { location: locationFilterValue }),
      });
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setIsSearching(false);
    }
  }, [
    activeFilter,
    searchTerm,
    sortBy,
    contentTab,
    selectedRoles,
    selectedSpecialties,
    locationFilterValue,
    categories,
    fetchPosts,
    fetchSavedPosts,
  ]);

  // createPost is now passed directly to PostComposer
  // Optimistic UI update is handled in usePosts hook

  /**
   * Handle post like/unlike with optimistic update
   * Updates UI immediately, then confirms with server
   */
  const handleLikePost = useCallback(
    async (postId) => {
      try {
        await toggleLikePost(postId);
      } catch (error) {
        console.error("Error toggling like:", error);
        // Error handling and rollback is done in the hook
      }
    },
    [toggleLikePost],
  );

  /**
   * Handle post deletion
   * Confirmation is handled by PostCard component's modal
   */
  const handleDeletePost = useCallback(
    async (postId) => {
      try {
        // Success/error toasts are handled inside the deletePost hook.
        await deletePost(postId);
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    },
    [deletePost],
  );

  /**
   * Handle post reporting
   * Removes post from view after reporting
   */
  const handleReportPost = useCallback(
    async (postId, reason) => {
      try {
        const result = await reportPost(postId, reason);
        if (result?.success) {
          toast.success("Post reported successfully");
        }
      } catch (error) {
        console.error("Error reporting post:", error);
      }
    },
    [reportPost],
  );

  /**
   * Handle comment count update
   * Called when comments are added/deleted
   */
  const handleCommentCountUpdate = useCallback(
    (postId, newCount) => {
      updateCommentCount(postId, newCount);
    },
    [updateCommentCount],
  );

  /**
   * Handle emoji reaction on post.
   * The usePosts hook updates the main `posts` state optimistically, but the
   * Saved tab renders from `savedPosts` — a separate slice. We mirror the
   * same patch onto `savedPosts` so reactions show up immediately on that tab.
   * @param {number} postId
   * @param {string} reactionType - 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry'
   */
  const patchPostReaction = useCallback((post, reactionType) => {
    if (!post || post._optimistic) return post;
    const currentReactions = post.reactions || {};
    const currentReactionTotal = Object.values(currentReactions).reduce(
      (sum, n) => sum + n,
      0,
    );
    // Legacy likes from mobile sit in `post.likes` above the per-reaction sums.
    const legacyLikesBaseline = Math.max(
      0,
      (post.likes || 0) - currentReactionTotal,
    );

    // Toggle off if user clicked the same reaction.
    if (post.userReaction === reactionType) {
      const next = { ...currentReactions };
      next[reactionType] = Math.max(0, (next[reactionType] || 0) - 1);
      const total = Object.values(next).reduce((s, n) => s + n, 0);
      return {
        ...post,
        userReaction: null,
        reactions: next,
        likes: total + legacyLikesBaseline,
        liked: false,
      };
    }

    // Switch or add a reaction.
    const next = { ...currentReactions };
    if (post.userReaction && post.userReaction !== reactionType) {
      next[post.userReaction] = Math.max(0, (next[post.userReaction] || 0) - 1);
    }
    next[reactionType] = (next[reactionType] || 0) + 1;
    const total = Object.values(next).reduce((s, n) => s + n, 0);
    return {
      ...post,
      userReaction: reactionType,
      reactions: next,
      likes: total + legacyLikesBaseline,
      liked: true,
    };
  }, []);

  /**
   * Keep savedPosts in sync when a card's save state toggles. The PostCard
   * handles its own bookmark icon; this only matters for the Saved tab,
   * where un-saving a post should remove it from the list immediately
   * (previously it stuck around until a refresh).
   */
  const handlePostSaveChange = useCallback((postId, isSavedNow) => {
    if (!isSavedNow) {
      setSavedPosts((prev) => prev.filter((p) => p.id !== postId));
    }
    // Saving from the For You/Following/All tabs doesn't need to mutate
    // savedPosts — the user will see it next time they open the tab.
  }, []);

  const handleReaction = useCallback(
    async (postId, reactionType) => {
      // Mirror the optimistic update onto savedPosts (the Saved-tab slice).
      // The hook handles the main `posts` slice; without this the Saved tab
      // shows no visual change even though the API call succeeds.
      setSavedPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? patchPostReaction(p, reactionType) : p,
        ),
      );
      try {
        await addOrUpdateReaction(postId, reactionType);
      } catch (error) {
        console.error("Error updating reaction:", error);
        // Roll back the savedPosts patch (same operation undoes itself).
        setSavedPosts((prev) =>
          prev.map((p) =>
            p.id === postId ? patchPostReaction(p, reactionType) : p,
          ),
        );
      }
    },
    [addOrUpdateReaction, patchPostReaction],
  );

  /**
   * Modal handlers for article detail
   */
  const handleShowArticle = useCallback((article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  }, []);

  const handleCloseArticle = useCallback(() => {
    setShowArticleModal(false);
    setSelectedArticle(null);
  }, []);

  /**
   * Share modal handlers
   * Accepts either a post object (from sidebar) or a post ID (from main feed)
   */
  const handleSharePost = useCallback(
    (postOrId) => {
      // Check if we received a post object or just an ID
      const post =
        typeof postOrId === "object" && postOrId !== null
          ? postOrId
          : posts?.find((p) => p?.id === postOrId);

      if (!post) return;

      setCurrentSharePost(post);
      setShareModal(true);
    },
    [posts],
  );

  const closeShareModal = useCallback(() => {
    setShareModal(false);
    setCurrentSharePost(null);
  }, []);

  /**
   * Handle category selection from popular topics
   * Updates both filter and triggers post fetch
   */
  const handleCategorySelect = useCallback((category) => {
    // Update the active filter to the selected category name
    setActiveFilter(category.category_name);
  }, []);

  /**
   * Share to social media platforms
   */
  const shareToSocialMedia = useCallback(
    (platform) => {
      if (!currentSharePost) return;

      const shareText = `Check out this post by ${
        currentSharePost.author
      }: ${currentSharePost.content?.substring(0, 100) || ""}...`;
      const shareUrl = window.location.href + "/" + currentSharePost.id;
      let url = "";

      switch (platform) {
        case "twitter":
          url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            shareText,
          )}&url=${encodeURIComponent(shareUrl)}`;
          break;
        case "facebook":
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            shareUrl,
          )}`;
          break;
        case "whatsapp":
          url = `https://wa.me/?text=${encodeURIComponent(
            shareText + " " + shareUrl,
          )}`;
          break;
        case "linkedin":
          url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            shareUrl,
          )}`;
          break;
        case "instagram":
          // Instagram doesn't have a web share API, so we copy the text + link
          // and try to open Instagram (mobile will open app, desktop opens web)
          const instagramText = `${shareText}\n\n${shareUrl}`;
          navigator.clipboard
            .writeText(instagramText)
            .then(async () => {
              closeShareModal();
              await Swal.fire({
                icon: "success",
                title: "Content Copied!",
                text:
                  "Post content and link copied to clipboard. You can now paste it on Instagram!",
                timer: 3000,
                showConfirmButton: false,
                customClass: { popup: "success-popup-modal" },
              });
              // After Swal closes, open Instagram
              window.open("https://www.instagram.com/", "_blank");
            })
            .catch(() => {
              toast.error("Failed to copy content");
            });
          return;
        case "copy":
          navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
              toast.success("Link copied to clipboard!");
            })
            .catch(() => {
              toast.error("Failed to copy link");
            });
          closeShareModal();
          return;
        default:
          return;
      }

      // Open social sharing URL in new window
      const popup = window.open(
        url,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      if (!popup) {
        toast.warning("Please allow popups for this site to share content");
      } else {
        // Intent URLs don't report back whether the user actually posted, so
        // we can only confirm the share window opened — not claim success.
        const platformName =
          platform.charAt(0).toUpperCase() + platform.slice(1);
        toast.info(`Opening ${platformName} to finish sharing…`);
      }

      closeShareModal();
    },
    [currentSharePost, closeShareModal],
  );

  // ============= COMPUTED VALUES =============
  // Build filter categories from API categories
  const filterCategories = [
    "All posts",
    ...(categories?.map((cat) => cat.category_name) || []),
  ];

  // Filtered posts for display — Saved tab pulls from its own state/API
  const isSavedTab = contentTab === "saved";
  const filteredPosts = isSavedTab ? savedPosts : posts || [];
  const feedLoading = isSavedTab ? savedLoading : postsLoading;
  const feedHasMore = isSavedTab ? savedHasMore : hasMore;

  // ============= RENDER =============
  return (
    <div className="community-page-content">
      <NoIndexSEO title={seoConfig.community.title} />

      {/* Page Header */}
      <div className="app-page-title">
        <div className="page-title-heading">
          <h3 className="page-title">Community</h3>
        </div>
      </div>

      {activeProfileNudge && !profileNudgeDismissed && (
        <div
          className="community-profile-nudge"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/profile")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/profile");
            }
          }}
        >
          <div className="community-profile-nudge-icon">
            <UserRound />
          </div>
          <div className="community-profile-nudge-body">
            <div className="community-profile-nudge-head">
              <span className="community-profile-nudge-eyebrow">
                Complete your profile
              </span>
              <span className="community-profile-nudge-pct">
                {profileCompletionPercent}%
              </span>
            </div>
            <div className="community-profile-nudge-bar-track">
              <div
                className="community-profile-nudge-bar-fill"
                style={{ width: `${profileCompletionPercent}%` }}
              />
            </div>
            <span className="community-profile-nudge-text">
              {activeProfileNudge.message}
            </span>
          </div>
          <button
            type="button"
            className="community-profile-nudge-close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissProfileNudge();
            }}
          >
            ×
          </button>
        </div>
      )}

      <Row className="g-4">
        <div className="main-content">
          {/* Header Section */}
          <div className="community-header">
            {/* Search Bar */}
            {/* <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSortChange={setSortBy}
              currentSort={sortBy}
              isSearching={isSearching}
            /> */}

            {/* Unified Community Card - profile, tabs, filters, trending, composer */}
            <div className="community-card">
              {/* Profile Strip (current user identity card) */}
              <div className="profile-strip" data-tour="cm-profile">
                <Image
                  src={
                    authUser?.profile_photo_path
                      ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${authUser.profile_photo_path}`
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          profileFullName,
                        )}&background=0195D3&color=fff&size=96`
                  }
                  alt={profileFullName}
                  roundedCircle
                  width={48}
                  height={48}
                  className="profile-strip-avatar"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      profileFullName,
                    )}&background=0195D3&color=fff&size=96`;
                  }}
                />
                <div className="profile-strip-info">
                  {/* Plan badge + upgrade-modal click commented out per request
                  <div
                    className="profile-strip-name"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowUpgradePlanModal(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowUpgradePlanModal(true);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                    title="View upgrade options"
                  >
                    {profileFullName}
                    <PlanShield
                      planName={authUser?.plan?.name}
                      onClick={() => setShowUpgradePlanModal(true)}
                    />
                  </div>
                  */}
                  <div className="profile-strip-name">{profileFullName}</div>
                  {profileMetaParts.length > 0 && (
                    <div className="profile-strip-meta">
                      {profileMetaParts.join(" · ")}
                    </div>
                  )}
                </div>
                {/* <button
                type="button"
                className="profile-strip-edit"
                onClick={() => navigate("/profile")}
              >
                Edit
              </button> */}
              </div>

              {/* Content Tabs: For You / Following / All / Saved */}
              <div className="content-tabs" role="tablist" data-tour="cm-tabbar">
                {[
                  { key: "for-you", label: "For You" },
                  { key: "following", label: "Following" },
                  { key: "all", label: "All" },
                  { key: "saved", label: "Saved" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={contentTab === tab.key}
                    data-tour={`cm-tab-${tab.key}`}
                    className={`content-tab ${
                      contentTab === tab.key ? "is-active" : ""
                    }`}
                    onClick={() => setContentTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Filters / Trending / Composer are hidden on the Saved tab —
                Saved uses its own API and has no composer flow. */}
              {contentTab !== "saved" && (
                <>
                  {/* Discussion Filters: Profession / Specialty / Topic (UI only, API pending) */}
                  <div className="discussion-filters" data-tour="cm-filters">
                    <ChipFilterDropdown
                      label="Profession"
                      dataTour="cm-filter-profession"
                      value={chipShortLabel(selectedRoles, "All")}
                      active /* always show the highlighted blue style */
                      options={roleLiveOptions}
                      selected={selectedRoles}
                      onApply={handleProfessionApply}
                      onLockedTap={handleLockedProfessionTap}
                    />
                    <ChipFilterDropdown
                      label="Specialty"
                      dataTour="cm-filter-specialty"
                      value={specialtyChipLabel(selectedSpecialties, "All")}
                      /* keep the default (grey) pill styling regardless of selection */
                      options={SPECIALTY_OPTIONS}
                      selected={selectedSpecialties}
                      onApply={handleSpecialtyApply}
                    />
                    {/* <button type="button" className="discussion-filter">
                <span className="filter-label">Topic:</span>
                <strong className="filter-value">All</strong>
                <ChevronDown size={14} className="filter-caret" />
              </button> */}
                    <LocationDropdown
                      value={locationChipValue}
                      dataTour="cm-filter-location"
                      /* keep grey/default pill regardless of selection */
                      states={allStates}
                      cities={allCities}
                      selectedState={selectedLocationState}
                      selectedCity={selectedLocationCity}
                      citiesLoading={locationCitiesLoading}
                      onSelectState={handleSelectLocationState}
                      onClearState={handleClearLocationState}
                      onSelectCity={handleSelectLocationCity}
                    />
                  </div>

                  {/* Category chips — drives the `category` param on /get-all-posts */}
                  <div className="trending-now">
                    <div className="trending-header">
                      <span className="trending-label">TRENDING NOW</span>
                      <button
                        ref={trendingSortRef}
                        type="button"
                        className="trending-period"
                        onClick={() => contentTab !== "for-you" && setShowTrendingSort((v) => !v)}
                        aria-haspopup="listbox"
                        aria-expanded={showTrendingSort}
                        disabled={contentTab === "for-you"}
                        title={contentTab === "for-you" ? "Sort is not available in For You feed" : undefined}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: contentTab === "for-you" ? "default" : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          opacity: contentTab === "for-you" ? 0.45 : 1,
                        }}
                      >
                        <span>{trendingSortLabel}</span>
                        <ChevronDown
                          size={14}
                          style={{
                            transform: showTrendingSort
                              ? "rotate(180deg)"
                              : "none",
                            transition: "transform 0.15s ease",
                          }}
                        />
                      </button>
                      {showTrendingSort &&
                        trendingSortPos &&
                        createPortal(
                          <div
                            ref={trendingSortMenuRef}
                            className="mmp-chip-popover"
                            role="listbox"
                            style={{
                              position: "fixed",
                              top: trendingSortPos.top,
                              left: trendingSortPos.left,
                              width: trendingSortPos.width,
                              zIndex: 9500,
                            }}
                          >
                            {trendingSortOptions.map((option) => {
                              const isSel = sortBy === option.value;
                              return (
                                <button
                                  key={option.value || "new"}
                                  type="button"
                                  role="option"
                                  aria-selected={isSel}
                                  onClick={() => {
                                    setSortBy(option.value);
                                    setShowTrendingSort(false);
                                  }}
                                  className={`chip-option ${
                                    isSel ? "is-selected" : ""
                                  }`}
                                >
                                  <span className="chip-option-label">
                                    {option.label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>,
                          document.body,
                        )}
                    </div>
                    <div className="trending-chips">
                      {/* "All posts" chip — no count from API */}
                      {(() => {
                        const isActive = activeFilter === "All posts";
                        return (
                          <button
                            key="All posts"
                            type="button"
                            className={`trending-chip ${
                              isActive ? "is-active" : ""
                            }`}
                            onClick={() => setActiveFilter("All posts")}
                          >
                            {isActive ? (
                              <span className="chip-dot" />
                            ) : (
                              <TrendingUp size={14} className="chip-icon" />
                            )}
                            <span className="chip-text">All posts</span>
                          </button>
                        );
                      })()}

                      {/* Real categories from the API — render with post_count */}
                      {(categories || []).map((cat) => {
                        const catName = cat.category_name;
                        const isActive = activeFilter === catName;
                        const count = cat.post_count;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            className={`trending-chip ${
                              isActive ? "is-active" : ""
                            }`}
                            onClick={() =>
                              setActiveFilter(isActive ? "All posts" : catName)
                            }
                          >
                            {isActive ? (
                              <span className="chip-dot" />
                            ) : (
                              <TrendingUp size={14} className="chip-icon" />
                            )}
                            <span className="chip-text">{catName}</span>
                            {typeof count === "number" && (
                              <span className="chip-count">· {count}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Post Composer */}
                  {/* <PostComposer
              createPost={createPost}
              categories={categories}
              isLoading={categoriesLoading}
              onPostSubmit={refresh}

            /> */}
                  <PostComposer
                    createPost={createPost}
                    categories={categories}
                    isLoading={categoriesLoading}
                    onPostSubmit={refreshCurrentTab}
                    activeTag={activeFilter}
                    onTagChange={setActiveFilter}
                    pendingPostText={pendingPostText}
                    onBeforeOpen={blockCreateIfFree}
                  />
                  {/* Free-tier weekly post allowance hint */}
                  {postsRemaining !== null && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: postsRemaining > 0 ? "var(--mmp-text-secondary)" : "var(--mmp-danger)",
                        margin: "6px 4px 0",
                      }}
                    >
                      {postsRemaining > 0 ? (
                        `${postsRemaining} free ${postsRemaining === 1 ? "post" : "posts"} left this week`
                      ) : (
                        <>
                          You're out of free posts this week —{" "}
                          <span
                            onClick={() =>
                              navigate("/profile/subscription", {
                                state: { source: "post_limit" },
                              })
                            }
                            style={{
                              color: "var(--mmp-accent)",
                              fontWeight: 600,
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            upgrade
                          </span>{" "}
                          for unlimited posting.
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* /Unified Community Card */}
          </div>

          {/* Poll Section — only renders when there is an active poll */}
          {activePoll && (
            <div className="community-poll-card">
              <div className="poll-header">
                <svg
                  className="poll-icon"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect x="2" y="10" width="3" height="8" rx="1" fill="currentColor" />
                  <rect x="8" y="6" width="3" height="12" rx="1" fill="currentColor" />
                  <rect x="14" y="2" width="3" height="16" rx="1" fill="currentColor" />
                </svg>
                <span className="poll-badge-label">Poll</span>
              </div>
              <div className="poll-question">{activePoll.question}</div>
              {activePoll.description && (
                <div className="poll-subtitle">{activePoll.description}</div>
              )}
              <div className="poll-options">
                {(activePoll.options || []).map((option) => {
                  const isSelected = pollSelectedOptions.includes(option.id);
                  const totalVotes = activePoll.total_votes || 0;
                  const pct = totalVotes > 0
                    ? Math.round((option.vote_count / totalVotes) * 100)
                    : 0;
                  const inputType = activePoll.is_multi_select ? "checkbox" : "radio";
                  return (
                    <label
                      key={option.id}
                      className={`poll-option${isSelected && !pollSubmitted ? " is-selected" : ""}${pollSubmitted ? " is-voted" : ""}`}
                    >
                      {pollSubmitted && (
                        <div
                          className="poll-option-fill"
                          style={{
                            width: `${pct}%`,
                            background: isSelected ? "var(--mmp-accent-bg)" : "var(--mmp-hover-bg)",
                          }}
                        />
                      )}
                      <input
                        type={inputType}
                        name="community-poll"
                        value={option.id}
                        checked={isSelected}
                        onChange={() => handlePollOptionChange(option.id)}
                        disabled={pollSubmitted}
                        aria-label={option.option_text}
                      />
                      <span className="poll-option-label">{option.option_text}</span>
                      {pollSubmitted && (
                        <span className="poll-option-pct">{pct}%</span>
                      )}
                    </label>
                  );
                })}
              </div>
              {pollSubmitted ? (
                <div className="poll-thanks">
                  Thanks for your feedback!{" "}
                  <span className="poll-total-votes">
                    {activePoll.total_votes || 0} vote{activePoll.total_votes !== 1 ? "s" : ""}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="poll-submit-btn"
                  onClick={handlePollSubmit}
                  disabled={pollSelectedOptions.length === 0 || pollSubmitting}
                >
                  {pollSubmitting ? "Submitting…" : "Submit"}
                </button>
              )}
            </div>
          )}

          {/* Loading State */}
          {feedLoading && filteredPosts.length === 0 && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading posts...</span>
              </div>
              <p className="text-muted mt-2">Loading posts...</p>
            </div>
          )}

          {/* Error State */}
          {/* {postsError && filteredPosts.length === 0 && (
            <div className="text-center py-5" role="alert">
              <h5>Error loading posts</h5>
              <p className="mb-3">{postsError}</p>
              <button
                className="btn btn-primary"
                onClick={() => fetchPosts()}
              >
                Try Again
              </button>
            </div>
          )} */}
          {/* {postsError && filteredPosts.length === 0 && (
            <div className="alert alert-danger" role="alert">
              <h5>Error loading posts</h5>
              <p>{postsError}</p>
              <button
                className="btn btn-primary"
                onClick={() => fetchPosts()}
              >
                Try Again
              </button>
            </div>
          )} */}

          {/* Posts Feed */}
          <div className="posts-feed">
            {filteredPosts
              .filter((post) => post && post.id)
              .map((post, index) => (
                <React.Fragment key={post.id}>
                  <PostCard
                    cardIndex={index}
                    post={post}
                    onLike={handleLikePost}
                    onReaction={handleReaction}
                    onDelete={handleDeletePost}
                    onReport={handleReportPost}
                    onCommentCountUpdate={handleCommentCountUpdate}
                    onShowArticle={handleShowArticle}
                    onShare={handleSharePost}
                    onSaveChange={handlePostSaveChange}
                    hideRepost={isSavedTab}
                    refreshPosts={refreshCurrentTab}
                    onOpenDetail={handleOpenPostDetail}
                  />
                  {index === 2 && (
                    <div
                      className="waitlist-card"
                      role="region"
                      aria-label="Suggest a profession"
                    >
                      <div className="waitlist-card__icon">
                        <Plus size={20} strokeWidth={2.5} />
                      </div>
                      <div className="waitlist-card__body" style={{ flex: 1 }}>
                        <div className="waitlist-card__title">
                          Not seeing your profession?
                        </div>
                        {profileData?.requested_role ? (
                          <div className="waitlist-card__desc">
                            Thanks — we've recorded{" "}
                            <strong>
                              {profileData.requested_role.role_name}
                            </strong>{" "}
                            as your suggestion.
                          </div>
                        ) : (
                          <>
                            <div className="waitlist-card__desc">
                              Tell us which role you'd like to see on MapMyPay.
                            </div>
                            <div
                              style={{ display: "flex", gap: 8, marginTop: 8 }}
                            >
                              <input
                                type="text"
                                value={wishlistRoleText}
                                onChange={(e) =>
                                  setWishlistRoleText(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleWishlistRoleSubmit();
                                }}
                                placeholder="e.g. Respiratory Therapist"
                                maxLength={150}
                                disabled={wishlistSubmitting}
                                style={{
                                  flex: 1,
                                  padding: "8px 12px",
                                  border: "1px solid var(--mmp-border)",
                                  borderRadius: 8,
                                  fontSize: 14,
                                  outline: "none",
                                  background: "var(--mmp-surface)",
                                }}
                              />
                              <button
                                type="button"
                                className="waitlist-card__cta"
                                onClick={handleWishlistRoleSubmit}
                                disabled={
                                  !wishlistRoleText.trim() || wishlistSubmitting
                                }
                              >
                                {wishlistSubmitting ? "Submitting…" : "Submit"}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}

            {/* Infinite Scroll Loader */}
            {filteredPosts.length > 0 && feedHasMore && (
              <div
                ref={loaderRef}
                className="infinite-scroll-loader text-center py-4"
                style={{ minHeight: "80px" }}
              >
                {feedLoading && (
                  <>
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">
                        Loading more posts...
                      </span>
                    </div>
                    <p
                      className="text-muted mt-2 mb-0"
                      style={{ fontSize: "14px" }}
                    >
                      Loading more posts...
                    </p>
                  </>
                )}
              </div>
            )}

            {/* End of Feed Indicator */}
            {filteredPosts.length > 0 && !feedHasMore && (
              <div className="text-center py-4">
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  🎉 You've reached the end! No more posts to load.
                </p>
              </div>
            )}

            {/* Empty State */}
            {!feedLoading && filteredPosts.length === 0 && (
              <div className="no-posts text-center">
                <h5 className="text-muted">No posts found</h5>
                <p className="text-muted">
                  {contentTab === "saved"
                    ? "You haven't saved any posts yet. Tap the bookmark on a post to save it here."
                    : contentTab === "following"
                    ? "No posts from people you follow yet. Follow more people to see their posts here."
                    : contentTab === "for-you"
                    ? "We don't have personalized posts for you yet. Check back soon!"
                    : searchTerm || activeFilter !== "All posts"
                    ? "Try adjusting your search or filter criteria."
                    : "Be the first to create a post!"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="sidebarcontent">
          <RightSidebar
            onShowArticle={handleShowArticle}
            onSharePost={handleSharePost}
            onLikePost={handleLikePost}
            onReaction={handleReaction}
            onReport={handleReportPost}
            onCategorySelect={handleCategorySelect}
          />
        </div>
      </Row>

      {/* Post Detail Modal — opens on post-card click, same data/UI/UX as the
          standalone /community/:id page but keeps the feed (and its scroll
          position) mounted underneath. */}
      <PostDetailModal
        show={showDetailModal}
        postId={detailPostId}
        planName={detailPostPlanName}
        onClose={handleClosePostDetail}
        onReaction={handleReaction}
        onCommentCountUpdate={handleCommentCountUpdate}
        onShare={handleSharePost}
        onReport={handleReportPost}
      />

      {/* Article Detail Modal */}
      <ArticleDetailModal
        show={showArticleModal}
        post={selectedArticle}
        onClose={handleCloseArticle}
        onLike={handleLikePost}
        onShare={handleSharePost}
        onCommentCountUpdate={handleCommentCountUpdate}
      />

      {/* Profession upgrade modal — same ComparisonModal Search.js uses
          for locked role taps so the UX/copy is identical. */}
      <ComparisonModal
        title={subText?.header}
        description={subText.desc}
        massage1={subText.massage1}
        pluspricem={subText.pluspricem}
        pluspricey={subText.pluspricey}
        prodesc={subText.desc2}
        promassage={subText.promassage}
        propricem={subText.propricem}
        propricey={subText.propricey}
        plan={subText.plan}
        lockedItemName={subText.lockedItemName}
        isVisible={subAlertCompModalVisible}
        eventSource={upgradeSource}
        onClose={() => setSubAlertCompModalVisible(false)}
        onPressYes={() => {
          navigate("/profile/subscription");
          setSubAlertCompModalVisible(false);
        }}
        yesText="Upgrade Now"
        noText="Cancel"
        extraMetaInfo={true}
      />

      {/* Weekly post allowance reached — free users get 1 post/week */}
      <RestrictionModal
        isVisible={showCreatePostRestriction}
        onClose={() => setShowCreatePostRestriction(false)}
        eventSource="post_limit"
        iconType="user-follow"
        heading="You've used your free post this week"
        subText="Free members can share 1 post per week. Upgrade to Pro to post as much as you like."
        infoText="Pro users can create posts and engage with the community without limits."
      />

      {/* Upgrade-plan modal — commented out per request
      <UpgradePlanModal
        show={showUpgradePlanModal}
        onClose={() => setShowUpgradePlanModal(false)}
      />
      */}

      {/* Share Modal */}
      {shareModal && currentSharePost && (
        <div
          className="modal-backdrop-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 99998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={closeShareModal}
        >
          <div
            className="modal-content-wrapper"
            style={{
              backgroundColor: "var(--mmp-surface)",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              zIndex: 99999,
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px",
                borderBottom: "1px solid var(--mmp-border)",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "var(--mmp-text-primary)",
                  margin: 0,
                  lineHeight: "24px",
                }}
              >
                Share Post
              </h3>
              <button
                onClick={closeShareModal}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  color: "var(--mmp-text-secondary)",
                  fontSize: "24px",
                  lineHeight: 1,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "0" }}>
              {/* Twitter */}
              <div
                onClick={() => shareToSocialMedia("twitter")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--mmp-border)",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    backgroundColor: "#1da1f2",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  <FaXTwitter size={15} />
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Share on Twitter
                </span>
              </div>

              {/* Facebook */}
              <div
                onClick={() => shareToSocialMedia("facebook")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--mmp-border)",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    backgroundColor: "#1877f2",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px",
                    fontWeight: "bold",
                  }}
                >
                  <FaFacebookF size={15} />
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Share on Facebook
                </span>
              </div>

              {/* WhatsApp */}
              <div
                onClick={() => shareToSocialMedia("whatsapp")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--mmp-border)",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    backgroundColor: "#25d366",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  <FaWhatsapp size={15} />
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Share on WhatsApp
                </span>
              </div>

              {/* LinkedIn */}
              <div
                onClick={() => shareToSocialMedia("linkedin")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--mmp-border)",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    backgroundColor: "#0077b5",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px",
                    fontWeight: "bold",
                  }}
                >
                  <FaLinkedinIn size={15} />
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Share on LinkedIn
                </span>
              </div>

              {/* Instagram */}
              <div
                onClick={() => shareToSocialMedia("instagram")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--mmp-border)",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <div
                  style={{
                    width: "22px",
                    height: "22px",
                    background:
                      "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px",
                    fontWeight: "bold",
                  }}
                >
                  <FaInstagram size={15} />
                </div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Share on Instagram
                </span>
              </div>

              {/* Copy Link */}
              <div
                onClick={() => shareToSocialMedia("copy")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  borderBottomLeftRadius: "12px",
                  borderBottomRightRadius: "12px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <Copy
                  style={{ width: "20px", height: "20px", color: "var(--mmp-text-secondary)" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Copy Link
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <GuidedTour
        isOpen={isTourOpen}
        steps={communityTourSteps}
        onDone={finishTour}
      />
    </div>
  );
};

export default Community;
